import React from "react";
import { ComposedChart, Bar, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Tile, Badge, StatusDot } from "../Primitives";
import { BRANCHES, REGIONS } from "../../lib/data";
import { PILOT_ALERTS } from "../../lib/dataPilot";
import { syn, combineOrigins } from "../../lib/origin.jsx";
import { useChartColors } from "../../lib/chartColors";
import { formatPeso } from "../../lib/utils";
import { ChartTip, tickStyle } from "./chartBits";

export function RegionCoverTile({ valuation }) {
  const C = useChartColors();
  const data = REGIONS.map(region => {
    const bs = BRANCHES.filter(b => b.region === region);
    const value = bs.reduce((s, b) => s + (valuation.valueByBranch[b.id] ?? 0), 0);
    const weight = bs.reduce((s, b) => s + b.inventoryValue, 0);
    const cover = weight ? bs.reduce((s, b) => s + b.daysOnHand * b.inventoryValue, 0) / weight : 0;
    return { region, value, cover: Math.round(cover * 10) / 10 };
  });
  const origin = {
    origin: combineOrigins([valuation.origin, "synthetic"]),
    source: `Inventory value: ${valuation.source} · Days of cover: synthetic sample dataset`,
    updatedAt: valuation.asOf,
    parts: [
      { origin: valuation.origin === "mixed" ? "actual" : valuation.origin, text: "Inventory value per region (shared valuation)" },
      { origin: "synthetic", text: "Days of cover per branch (sample dataset)" },
    ],
  };
  return (
    <Tile className="col-span-12 lg:col-span-6" testid="region-inventory" title="Inventory Value & Days of Cover by Region" origin={origin}>
      <div style={{ height: 240 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 0, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="region" tick={tickStyle} stroke={C.borderStrong} />
            <YAxis yAxisId="v" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
            <YAxis yAxisId="d" orientation="right" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${v}d`} />
            <Tooltip content={<ChartTip formatter={(v, p) => (p.dataKey === "cover" ? `${v} days` : formatPeso(v, { compact: true }))} />} cursor={{ fill: C.surface2 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar yAxisId="v" dataKey="value" name="Inventory value" fill={C.chart[0]} radius={[2, 2, 0, 0]} isAnimationActive={false} />
            <Line yAxisId="d" dataKey="cover" name="Days of cover" stroke={C.warning} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Tile>
  );
}

export function PilotAlertsTile() {
  const critical = PILOT_ALERTS.filter(a => a.severity === "critical").length;
  return (
    <Tile className="col-span-12 lg:col-span-6" testid="critical-alerts" title="Inventory, Demand & Supply Alerts"
          origin={syn("alert stream (stock, demand, supplier)")}
          action={<Badge status="critical">{critical} Critical</Badge>}>
      <div className="space-y-0 divide-y divide-border -mx-4 -mt-4">
        {PILOT_ALERTS.slice(0, 7).map(a => (
          <div key={a.id} data-testid={`alert-${a.id}`} className="px-4 py-2.5 flex items-start gap-3 text-sm">
            <StatusDot status={a.severity} className="mt-1.5" />
            <div className="flex-1">
              <div className="font-medium leading-snug">{a.text}</div>
              <div className="text-[11px] text-muted mt-0.5 flex items-center gap-2">
                <span className="font-mono-tabular">{a.timeAgo}</span>
                <span className="text-subtle">·</span>
                <span className="uppercase tracking-wider">{a.kind}</span>
              </div>
            </div>
            <Badge status={a.severity}>{a.severity}</Badge>
          </div>
        ))}
      </div>
    </Tile>
  );
}
